import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { BrickWallFire } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { getRegionNatIp } from '@/constants/regions';

/**
 * Props for the FirewallHelpDialog component
 */
interface FirewallHelpDialogProps {
  /** Whether the dialog is open */
  open: boolean;
  /** Callback when the dialog open state changes */
  onOpenChange: (open: boolean) => void;
  /** The region ID to look up the NAT gateway IP address */
  regionId?: string | null;
}

/**
 * Reusable dialog that explains Azure SQL firewall configuration options.
 * Shows the static NAT gateway IP address for the profile's region when available.
 */
export function FirewallHelpDialog({ open, onOpenChange, regionId }: FirewallHelpDialogProps) {
  const { t } = useLanguage();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <BrickWallFire className="h-5 w-5" />
            {t.wizard.target.firewallTitle}
          </DialogTitle>
          <DialogDescription>{t.wizard.target.firewallDescription}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <span className="font-medium">{t.wizard.target.firewallOptionIpRecommended}</span>{' '}
            <span className="text-sm text-muted-foreground">
              {t.wizard.target.firewallOptionIpDescription}
            </span>
            {regionId && getRegionNatIp(regionId) ? (
              <code className="mt-2 block rounded bg-muted px-3 py-2 text-sm font-mono select-all">
                {getRegionNatIp(regionId)}
              </code>
            ) : (
              <p className="mt-1 text-sm text-muted-foreground">
                {t.wizard.target.firewallIpUnavailable}
              </p>
            )}
          </div>
          <div>
            <span className="font-medium">{t.wizard.target.firewallOptionAzureLabel}</span>{' '}
            <span className="text-sm text-muted-foreground">
              {t.wizard.target.firewallOptionAzure}
            </span>
          </div>
        </div>
        <div className="flex justify-end">
          <Button variant="outline" onClick={() => { onOpenChange(false); }}>
            {t.wizard.target.firewallClose}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
